import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { IApiErrorResponse } from '@core/models/api-response.model';
import { IApiStandardResponse } from '@core/models/api/api.models';
import {
    ILoginCredentials,
    IResetPWD,
    IResetPWDResponse,
    IToken,
    RESET_PWD_APP_SOURCE
} from '@core/models/auth.model';
import { I_API_POST_verificaRegistrazione, IRegistrazione, IVerificaRegistrazione } from '@core/models/signup.model';
import { IUser } from '@core/models/user.model';
import { ApiService } from '@core/services/api/api.service';
import { Observable, of, throwError } from 'rxjs';
import { catchError, first, map, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { Console } from './logger.service';
import { AppStore } from './store/app.store';

/**
 * Servizio per la gestione dell'autenticazione e delle operazioni relative all'utente.
 *
 * Questo servizio si occupa di tutte le operazioni di autenticazione e autorizzazione,
 * inclusi login, logout, refresh del token, registrazione utente e gestione password.
 *
 * Funzionalità offerte:
 * - Autenticazione utente (login/logout)
 * - Gestione e refresh dei token di accesso
 * - Registrazione e verifica di nuovi utenti
 * - Gestione completa del ciclo di vita delle password (reset, modifica, verifica)
 * - Verifica della disponibilità email per nuove registrazioni
 * - Gestione delle sessioni utente
 *
 * @example
 * constructor(private authService: AuthService) {}
 *
 * // Esempio di login
 * this.authService.login(credentials).subscribe({
 *   next: (token) => console.log('Token ricevuto:', token),
 *   error: (error) => console.error('Errore login:', error)
 * });
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly log_label = 'AuthService';
  private readonly apiService = inject(ApiService);
  private readonly appStore = inject(AppStore);
  private readonly router = inject(Router);

  // Signal per lo stato dell'utente autenticato (mantenuto per compatibilità)
  readonly currentUser = signal<IUser | null>(null);
  readonly isAuthenticated = signal<boolean>(false);

  constructor() {
    // Verifica se c'è un token salvato al caricamento
    this.checkAuthStatus();
  }

  /**
   * Verifica lo stato di autenticazione al caricamento
   */
  private checkAuthStatus(): void {
    const token = this.appStore.token();
    const user = this.appStore.user();
    
    if (token && user) {
      this.currentUser.set(user);
      this.isAuthenticated.set(true);
    }
  }

  /**
   * Recupera i dati dell'utente corrente autenticato.
   * 
   * Gestisce automaticamente gli errori di autenticazione, rete e validazione.
   * In caso di token non valido (401), effettua automaticamente il logout.
   *
   * @returns Observable contenente i dati dell'utente
   * @throws {Error} Se la richiesta fallisce, l'utente non è autenticato o si verificano errori di rete
   */
  me(): Observable<IUser> {
    return this.apiService.get<IUser>('user').pipe(
      first(),
      map((response) => response),
      tap((user) => {
        this.appStore.setUser(user);
        this.currentUser.set(user);
        this.isAuthenticated.set(true);
      }),
      catchError((error: IApiErrorResponse) => {
        Console.log(`[${this.log_label}] Errore nel recupero dati utente:`, error, 'err');
        
        // Gestione specifica per errori di autenticazione
        const statusCode = (error as any)?.status_code ?? (error as any)?.status;
        if (statusCode === 401) {
          // Prova a fare refresh del token solo se rememberMe è attivo
          // Se rememberMe non è attivo, esegui direttamente il logout
          const rememberMe = this.appStore.rememberMe();
          if (!rememberMe) {
            this.logout().subscribe();
            return throwError(() => new Error('Sessione scaduta, effettua nuovamente il login'));
          }
          
          // Prova a fare refresh del token (se possibile) e ritenta /user.
          // Se fallisce, allora esegui logout e propaga errore.
          return this.refreshToken().pipe(
            switchMap(() =>
              this.apiService.get<IUser>('user').pipe(
                first(),
                tap((user) => {
                  this.appStore.setUser(user);
                  this.currentUser.set(user);
                  this.isAuthenticated.set(true);
                })
              )
            ),
            catchError((refreshErr) => {
              this.logout().subscribe();
              return throwError(() => new Error('Sessione scaduta, effettua nuovamente il login'));
            })
          );
        }
        
        // Per tutti gli altri errori (500+, 422, ecc.), resetta lo stato di autenticazione
        // perché senza dati utente validi non possiamo considerare l'utente autenticato
        Console.log(`[${this.log_label}] Errore nel recupero dati utente (status: ${error.status_code}), reset autenticazione`, '', 'warn');
        // Resetta solo l'utente (non il token, perché potrebbe essere ancora valido)
        // Questo fa sì che isAuthenticated() diventi false (richiede sia token che user)
        this.appStore.user.set(null);
        this.currentUser.set(null);
        this.isAuthenticated.set(false);
        
        // Gestione errori di rete/server
        if (error.status_code && error.status_code >= 500) {
          return throwError(() => new Error('Errore del server, riprova più tardi'));
        }
        
        // Gestione errori di validazione
        if (error.status_code === 422) {
          return throwError(() => new Error('Dati utente non validi'));
        }
        
        // Gestione errori generici
        const errorMessage = error.message || 'Errore nel recupero dati utente';
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  /**
   * Effettua il login dell'utente con le credenziali fornite.
   *
   * @param loginData Le credenziali di accesso dell'utente (username, password, device_id)
   * @returns Observable contenente il token di autenticazione
   * @throws {Error} Se le credenziali non sono valide o la richiesta fallisce
   */
  login(loginData: ILoginCredentials): Observable<IToken> {
    Console.log('AuthService.login - Before device_id assignment:', {
      device_id: this.appStore.device_id(),
      loginData: loginData
    }, 'info');

    // Assicura che device_name sia impostato con device_id dallo store
    if (!loginData.device_name && this.appStore.device_id()) {
      loginData.device_name = this.appStore.device_id() || '';
    }

    Console.log('AuthService.login - After device_id assignment:', {
      device_name: loginData.device_name,
      loginData: loginData
    }, 'info');

    return this.apiService.post<IToken, ILoginCredentials>('login', loginData).pipe(
      first(),
      tap((token) => {
        Console.log('AuthService.login - Token received:', token, 'info');
        this.appStore.setToken(token);
        this.isAuthenticated.set(true);
      }),
      map((token) => {
        Console.log('AuthService.login - Handling token:', token, 'info');
        return this.handleToken(token);
      }),
      catchError((error: IApiErrorResponse) => {
        Console.log('AuthService.login - Error details:', error, 'err');
        return throwError(() => error);
      })
    );
  }

  /**
   * Effettua il logout dell'utente e pulisce i dati di sessione.
   *
   * Chiama sempre l'API di logout per invalidare il token sul backend (repository),
   * anche se il token potrebbe essere scaduto. Il backend gestirà l'invalidazione
   * del token se riceve la richiesta.
   *
   * In caso di errore durante il logout (es. token scaduto), procede comunque con
   * la pulizia locale e il reindirizzamento alla pagina di login.
   *
   * @returns Observable che completa quando il logout è terminato
   */
  logout(): Observable<unknown> {
    Console.log('AuthService.logout - Starting logout process', '', 'info');

    // Chiama sempre l'API di logout per invalidare il token sul backend
    // Anche se il token è scaduto, il backend potrebbe comunque invalidarlo se riceve la richiesta
    return this.callLogoutAPI();
  }

  /**
   * Chiama l'API di logout e gestisce la pulizia locale.
   * Metodo privato utilizzato da logout() per evitare duplicazione del codice.
   *
   * @private
   * @returns Observable che completa quando il logout è terminato
   */
  private callLogoutAPI(): Observable<unknown> {
    return this.apiService.post<IApiStandardResponse<unknown>, void>('/logout', undefined).pipe(
      tap((response) => {
        Console.log('AuthService.logout - API response received:', response, 'info');
      }),
      map((response) => {
        Console.log('AuthService.logout - Processing response', '', 'info');
        return response || {};
      }),
      first(),
      tap(() => {
        Console.log('AuthService.logout - Clearing auth and navigating', '', 'info');
        this.appStore.clearAuth();
        this.currentUser.set(null);
        this.isAuthenticated.set(false);
        if (environment.ENABLE_DEBUG_ALERTS) {
          alert('auth-app : Redirect a /login (logout successo)');
        }
        this.router.navigateByUrl('/login', { replaceUrl: true });
      }),
      catchError((error) => {
        Console.log('AuthService.logout - Error occurred:', error, 'err');
        // Procedi comunque con la pulizia locale anche in caso di errore
        // Il token potrebbe essere già stato invalidato sul backend o essere scaduto
        this.appStore.clearAuth();
        this.currentUser.set(null);
        this.isAuthenticated.set(false);
        if (environment.ENABLE_DEBUG_ALERTS) {
          alert('auth-app : Redirect a /login (logout con errore)');
        }
        this.router.navigateByUrl('/login', { replaceUrl: true });
        return of(null);
      })
    );
  }

  /**
   * Aggiorna il token di autenticazione utilizzando il refresh token corrente.
   *
   * **Operazioni eseguite:**
   * 1. Recupera il token corrente dallo store dell'applicazione
   * 2. Estrae il refresh_token dal token memorizzato
   * 3. Invia una richiesta API per ottenere un nuovo token di accesso
   * 4. Salva automaticamente il nuovo token nello store tramite handleToken()
   *
   * **Gestione degli errori:**
   * - Se non viene trovato alcun token locale, lancia un errore
   * - Se riceve un errore 401 (Unauthorized), significa che anche il refresh token è scaduto
   *   e attiva automaticamente la procedura di logout con pulizia completa della sessione
   * - Per altri errori HTTP, rilancia l'eccezione per permettere la gestione upstream
   *
   * @returns Observable contenente il nuovo token di autenticazione aggiornato
   * @throws {Error} Se non viene trovato un token locale valido
   * @throws {Error} Se la richiesta di refresh fallisce per motivi diversi dall'autorizzazione
   *
   * @example
   * ```typescript
   * this.authService.refreshToken().subscribe({
   *   next: (newToken) => Console.log('Token aggiornato:', newToken, 'info'),
   *   error: (error) => Console.log('Errore nel refresh:', error, 'err')
   * });
   * ```
   */
  refreshToken(): Observable<IToken> {
    const token = this.appStore.token();
    const rememberMe = this.appStore.rememberMe();
    
    // Il refresh token deve essere eseguito solo quando rememberMe è flaggato
    if (!rememberMe) {
      // Se rememberMe non è attivo, non è consentito usare il refresh token:
      // esegui subito il logout e scrivi solo un messaggio in console (nessun errore emesso).
      Console.log('AuthService.refreshToken - chiamato con rememberMe=false, eseguo logout senza refresh', '', 'info');
      this.logout().subscribe();
      // Completa senza emettere errori: il chiamante non deve gestire eccezioni per questo caso "voluto".
      return of(token as IToken);
    }
    
    return of(token).pipe(
      map((itoken) => {
        if (!itoken || !itoken.refresh_token) {
          throw new Error('No token found');
        }
        return itoken.refresh_token;
      }),
      switchMap((refreshToken) => {
        return this.apiService.post<IToken, { refresh_token: string }>(
          'refresh-token',
          { refresh_token: refreshToken }
        ).pipe(
          catchError((error: IApiErrorResponse) => {
            // Se ricevo un 401, significa che anche il refresh token è scaduto
            if (error.status_code === 401) {
              Console.log('AuthService.refreshToken - 401 received, calling logout', '', 'warn');
              // Eseguo il logout e restituisco un Observable vuoto
              this.logout().subscribe();
              return throwError(() => new Error('Token scaduto, logout effettuato'));
            }
            // Per altri errori, rilancia l'eccezione
            return throwError(() => error);
          })
        );
      }),
      map((responseRefreshToken) => this.handleToken(responseRefreshToken)),
      catchError((error) => {
        // Se arriva qui non riesco a recuperare il token e faccio un logout
        Console.log('AuthService.refreshToken - General error:', error, 'err');
        this.logout().subscribe();
        return throwError(() => error);
      })
    );
  }

  /**
   * Gestisce il salvataggio del token nel sistema.
   *
   * Verifica la validità del token e lo memorizza nello store dell'applicazione.
   *
   * @param response Il token da salvare
   * @returns Il token validato e salvato
   * @throws {Error} Se il token non è valido o mancante
   *
   * @private
   */
  private handleToken(response: IToken): IToken {
    if (!response.token || !response.token_id || !response.token_type) {
      throw new Error('No token found');
    }
    this.appStore.setToken(response);
    return response;
  }

  /**
   * Invia una richiesta per il reset della password.
   *
   * Invia un'email all'utente con le istruzioni per reimpostare la password.
   *
   * @param data I dati necessari per il reset della password (username, device_name, "sso" per definire l'app che chiede la password)
   * @returns Observable contenente il messaggio di risposta (stringa, perché ApiService estrae response.data)
   * @throws {Error} Se la richiesta fallisce o i dati non sono validi
   */
  resetPWD(data: IResetPWD): Observable<string> {
    const payload: IResetPWD = {
      ...data,
      app_source: data.app_source ?? RESET_PWD_APP_SOURCE
    };
    return this.apiService.post<string, IResetPWD>('/password/email', payload).pipe(
      catchError((error: IApiErrorResponse) => {
        const errorMessage = error.message || 'Errore nel reset della password.';
        Console.log(errorMessage, error, 'err');
        return throwError(() => ({
          ...error,
          message: errorMessage
        }));
      })
    );
  }

  /**
   * Alias per resetPWD per compatibilità con il componente forgot-password.
   *
   * @param request I dati necessari per il reset della password (username, device_name)
   * @returns Observable contenente il messaggio di risposta del server
   */
  forgotPassword(request: IResetPWD): Observable<string> {
    return this.resetPWD(request).pipe(
      map((message) => message || 'Richiesta inviata con successo')
    );
  }

  /**
   * Verifica i dati di registrazione dell'utente.
   *
   * Controlla la validità dei dati forniti per la registrazione.
   *
   * @param input I dati di verifica della registrazione
   * @returns Observable contenente la risposta di verifica
   * @throws {Error} Se la verifica fallisce o i dati non sono validi
   */
  verificaRegistrazione(input: IVerificaRegistrazione): Observable<I_API_POST_verificaRegistrazione> {
    return this.apiService.post<I_API_POST_verificaRegistrazione, IVerificaRegistrazione>(
      'user/registrazione/verifica',
      input
    ).pipe(
      catchError((error: IApiErrorResponse) => {
        Console.log('Errore nella verifica della registrazione', error, 'err');
        return throwError(() => error);
      })
    );
  }

  /**
  * Verifica se un'email è disponibile per la registrazione di un nuovo utente.
  *
  * Controlla che l'email non sia già utilizzata nel sistema.
  *
  * @param email L'email da verificare
  * @returns Observable contenente la risposta di disponibilità
  * @throws {Error} Se l'email è già in uso o la verifica fallisce
  */
 verificaNuovaUtenza(email: string): Observable<IApiStandardResponse<unknown>> {
   return this.apiService
     .post<IApiStandardResponse<unknown>, { email: string }>('user/registrazione/disponibile', {
       email: email,
     })
     .pipe(
       map((response: IApiStandardResponse<unknown>) => {
         return response;
       }),
       catchError((error: IApiErrorResponse) => {
         error.message = error?.message || ' Email già utilizzata.';
         return throwError(() => error);
       })
     );
  }

  /**
   * Verifica se il codice fiscale è già presente nel sistema.
   *
   * Utilizza lo stesso endpoint di verifica disponibilità utenza passando
   * il codice fiscale come campo di input.
   *
   * @param codiceFiscale Il codice fiscale da controllare
   * @returns Observable contenente l'esito della verifica
   * @throws {Error} Se il codice fiscale risulta già presente o la verifica fallisce
   */
  verificaEsistenzaCodiceFiscale(payload: { codice_fiscale: string; nome: string; cognome: string }): Observable<IApiStandardResponse<unknown>> {
    return this.apiService
      .post<IApiStandardResponse<unknown>, { codice_fiscale: string; nome: string; cognome: string }>('user/registrazione/codice-fiscale-disponibile', {
        codice_fiscale: payload.codice_fiscale,
        nome: payload.nome,
        cognome: payload.cognome
      })
      .pipe(
        map((response: IApiStandardResponse<unknown>) => {
          return response;
        }),
        catchError((error: IApiErrorResponse) => {
          error.message = error?.message || ' Codice fiscale già utilizzato.';
          return throwError(() => error);
        })
      );
  }

  /**
   * Verifica se il numero di cellulare è già presente nel sistema.
   *
   * Utilizza lo stesso endpoint di verifica disponibilità utenza passando
   * il numero di cellulare come campo di input.
   *
   * @param nrCellulare Il numero di cellulare da controllare
   * @returns Observable contenente l'esito della verifica
   * @throws {Error} Se il numero di cellulare risulta già presente o la verifica fallisce
   */
  verificaEsistenzaNrCellulare(nrCellulare: string): Observable<IApiStandardResponse<unknown>> {
    return this.apiService
      .post<IApiStandardResponse<unknown>, { cellulare: string }>('user/registrazione/cellulare-disponibile', {
        cellulare: nrCellulare,
      })
      .pipe(
        map((response: IApiStandardResponse<unknown>) => {
          return response;
        }),
        catchError((error: IApiErrorResponse) => {
          error.message = error?.message || ' Numero di cellulare già utilizzato.';
          return throwError(() => error);
        })
      );
  }

   /**
  * Effettua la registrazione di un nuovo utente nel sistema.
  *
  * Crea un nuovo account utente con i dati forniti.
  *
  * @param input I dati di registrazione dell'utente
  * @returns Observable contenente la risposta della registrazione
  * @throws {Error} Se la registrazione fallisce o i dati non sono validi
  */
  registrazione(input: IRegistrazione): Observable<IApiStandardResponse<unknown>> {
    let log_pref = this.log_label + '.registrazione() ';
    return this.apiService
      .post<IApiStandardResponse<unknown>, IRegistrazione>('user/registrazione', input)
      .pipe(
        map((response: IApiStandardResponse<unknown>) => {
          return response;
        }),
        catchError((error: IApiErrorResponse) => {
          error.message =
            log_pref +
            (error?.message || ' Errore nella registrazione utente.');
          return throwError(() => error);
        })
      );
  }
    
   /**
  * Effettua la registrazione di un nuovo utente nel sistema.
  *
  * Crea un nuovo account utente con i dati forniti.
  *
  * @param input I dati di registrazione dell'utente
  * @returns Observable contenente la risposta della registrazione
  * @throws {Error} Se la registrazione fallisce o i dati non sono validi
  */
  registrazioneSemplificata(input: IRegistrazione): Observable<IApiStandardResponse<unknown>> {
    let log_pref = this.log_label + '.registrazione() ';
    return this.apiService
      .post<IApiStandardResponse<unknown>, IRegistrazione>('user/registrazione/semplice', input)
      .pipe(
        map((response: IApiStandardResponse<unknown>) => {
          return response;
        }),
        catchError((error: IApiErrorResponse) => {
          error.message =
            log_pref +
            (error?.message || ' Errore nella registrazione utente.');
          return throwError(() => error);
        })
      );
  }
    /**
   * Modifica la password dell'utente autenticato.
   *
   * Richiede la nuova password e la conferma della password.
   *
   * @param credentials Le credenziali per la modifica della password (password e password_confirmation)
   * @returns Observable contenente il messaggio della risposta (ApiService estrae già data dalla risposta standard)
   * @throws {IApiErrorResponse} Se la modifica fallisce o le credenziali non sono valide
   */
  changePassword(credentials: { password: string; password_confirmation: string }): Observable<string> {
    const log_pref = this.log_label + '.changePassword() ';
    return this.apiService.post<string, { password: string; password_confirmation: string }>(
      '/password/modifica',
      credentials
    ).pipe(
      catchError((error: IApiErrorResponse) => {
        const errorMessage = log_pref + (error?.message || 'Errore nella modifica della password.');
        Console.log(errorMessage, error, 'err');
        return throwError(() => error);
      })
    );
  }

  /**
   * Effettua il reset della password dell'utente.
   *
   * Utilizza il token di reset ricevuto via email per impostare una nuova password.
   *
   * @param credentials I dati necessari per il reset (email, password, conferma password, token)
   * @returns Observable contenente la risposta del reset
   * @throws {Error} Se il reset fallisce o i dati non sono validi
   */
  resetPassword(credentials: {
    email: string;
    password: string;
    password_confirmation: string;
    token: string;
  }): Observable<IResetPWDResponse> {
    const log_pref = this.log_label + '.resetPassword() ';
    return this.apiService.post<IResetPWDResponse, typeof credentials>(
      '/password/reset',
      credentials
    ).pipe(
      catchError((error: IApiErrorResponse) => {
        const errorMessage = log_pref + (error?.message || 'Errore nel reset della password.');
        Console.log(errorMessage, error, 'err');
        return throwError(() => ({
          ...error,
          message: errorMessage
        }));
      })
    );
  }

  /**
   * Verifica se l'utente è autenticato
   */
  getIsAuthenticated(): boolean {
    return this.isAuthenticated();
  }

  /**
   * Ottiene l'utente corrente
   */
  getCurrentUser(): IUser | null {
    return this.currentUser();
  }
}
